import React, { useState } from 'react';
import { Button, Col, Form, Input, Row, Select, Space, Switch, Table } from 'antd';
import { DownOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { PageWithFiltersContentContainer } from '../page/page.styles';
import PageWithFilters from '../page/page-with-filters';
import useGetCardAffinityGroups from '../../hooks/CardAffinityGroup/useGetCardAffinityGroups';
import { ICardAffinityGroup } from '../../interfaces/get-card-affinity-groups';
import type { ColumnsType } from 'antd/es/table';

type SearchAffinityGroupsFilesFilters = {
  id: number;
  name: string;
  active: boolean;
  sort: string;
};

const Filters = ({ onSubmit }: { onSubmit: (data: {}) => void }) => {
  const [form] = Form.useForm();
  const [expand, setExpand] = useState(false);
  const [active, setActive] = useState(false);

  const onFinish = (values: SearchAffinityGroupsFilesFilters) => {
    onSubmit({ active: active, ...values });
  };

  const onReset = () => {
    onSubmit({});
  };

  return (
    <Form form={form} name="advanced_search" style={{ maxWidth: 'none' }} onFinish={onFinish} onReset={onReset}>
      <Row gutter={24}>
        <Col span={8}>
          <Form.Item<SearchAffinityGroupsFilesFilters> name={'name'} label={'Nombre'}>
            <Input />
          </Form.Item>
        </Col>
        {!expand && (
          <Col span={16}>
            <div style={{ textAlign: 'right' }}>
              <Space size="small">
                <Button type="primary" htmlType="submit">
                  Buscar
                </Button>
                <Button htmlType="reset">Limpiar</Button>
                <a
                  style={{ fontSize: 12 }}
                  onClick={() => {
                    setExpand(!expand);
                  }}
                >
                  <DownOutlined rotate={expand ? 180 : 0} /> Más
                </a>
              </Space>
            </div>
          </Col>
        )}
        {expand && (
          <>
            <Col span={4}>
              <Form.Item<SearchAffinityGroupsFilesFilters>
                name={'active'}
                label={'Activo'}
                style={{ textAlign: 'center' }}
              >
                <Switch
                  onChange={() => {
                    setActive(!active);
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item<SearchAffinityGroupsFilesFilters> name={'sort'} label={'Ordenar por'}>
                <Select
                  placeholder="Seleccione..."
                  options={[
                    {
                      label: 'Id',
                      value: 'id',
                    },
                    {
                      label: 'Nombre',
                      value: 'name',
                    },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={24} style={{ marginBottom: '1.5rem' }}>
              <div style={{ textAlign: 'right' }}>
                <Space size="small">
                  <Button type="primary" htmlType="submit">
                    Buscar
                  </Button>
                  <Button htmlType="reset">Limpiar</Button>
                  <a
                    style={{ fontSize: 12 }}
                    onClick={() => {
                      setExpand(!expand);
                    }}
                  >
                    <DownOutlined rotate={expand ? 180 : 0} /> Menos
                  </a>
                </Space>
              </div>
            </Col>
          </>
        )}
      </Row>
    </Form>
  );
};

const CardAffinityGroups = () => {
  const { issuer } = useParams<{ issuer: string }>();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    name: '',
    active: true,
    sort: '',
  });

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });


  const { affinityGroups, pageInfo, loading, getCardAffinityGroups } = useGetCardAffinityGroups(
    paginationModel.page,
    paginationModel.pageSize,
    filters
  );


  const handlePaginationChange = (page: number, pageSize: number) => {
    setPaginationModel({ page: page - 1, pageSize });
  };

  const handleSearchClick = (data: SearchAffinityGroupsFilesFilters) => {
    setFilters({
      active: data?.active,
      name: data?.name,
      sort: data?.sort,
    });
  };

  const columns: ColumnsType<ICardAffinityGroup> = [
    {
      dataIndex: 'id',
      key: 'id',
      title: 'Id',
    },
    {
      dataIndex: 'name',
      key: 'name',
      title: 'Nombre',
    },
    {
      dataIndex: 'start_date',
      key: 'start_date',
      title: 'Fecha desde',
    },
    {
      dataIndex: 'end_date',
      key: 'end_date',
      title: 'Fecha hasta',
    },
    {
      dataIndex: 'active',
      key: 'active',
      title: 'Activo',
      render: (_, record) => {
        return record?.active ? 'Si' : 'No';
      },
    },
    {
      dataIndex: 'actions',
      key: 'actions',
      title: 'Acciones',
      render: (_, record) => {
        return (
          <Button
            onClick={(e) => {
              e.preventDefault();
              navigate(`/${issuer}/card-affinity-groups/${record?.id}`);
            }}
            size="small"
            type="link"
            icon={<EyeOutlined />}
          >
            Detalles
          </Button>
        );
      },
    },
  ];

  const NewCardAffinityGroup = () => {
    return (
      <Button icon={<PlusOutlined />} onClick={() =>  navigate(`/${issuer}/card-affinity-groups/new`) } type="primary">
        Nuevo
      </Button>
    );
  };

  // @ts-ignore
  return (
    <PageWithFilters title="Grupos de afinidad">
      <PageWithFiltersContentContainer>
        <Filters onSubmit={handleSearchClick} />
      </PageWithFiltersContentContainer>
      <PageWithFiltersContentContainer>
        <div style={{ display: 'flex', marginBottom: '1.5rem', justifyContent: 'flex-end', gap: '1rem' }}>
          <NewCardAffinityGroup />
        </div>
        <Table
          // @ts-ignore
          dataSource={affinityGroups}
          scroll={{ x: true }}
          columns={columns}
          loading={loading}
          pagination={{
            total: pageInfo?.total_rows,
            showTotal: (total) => pageInfo?.total !== undefined ? `Total: ${total}` : ``,
            defaultPageSize: 10,
            defaultCurrent: 1,
            onChange: handlePaginationChange,
            pageSizeOptions: ['10', '25', '50', '100'],
            showSizeChanger: true,
          }}
        />
      </PageWithFiltersContentContainer>
    </PageWithFilters>
  );
};

// @ts-ignore
export default CardAffinityGroups;
